import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BookOpen, Check, Clock, Download, FileText, Lock, Trash2, Upload, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { classEntryApi } from '../../api/examApi';
import { StudentLayout } from '../../components/layout/StudentLayout';
import UploadHint from '../../components/UploadHint';
import { parseApiError } from '../../lib/apiErrors';
import { checkFileSize, isPdf } from '../../lib/uploadLimits';

const HOMEWORK_BADGE = {
  done: { label: 'Homework Done', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300', icon: Check },
  not_done: { label: 'Homework Not Done', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300', icon: X },
  pending: { label: 'Homework Pending', color: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300', icon: Clock },
};

const DEFAULT_MAX_FILES = 3;

const NOT_PDF_MESSAGE =
  'Homework must be a PDF. On your phone, use a scanner app (e.g. Google Drive → Scan, or iPhone Notes → Scan Documents) to save it as a PDF.';

const fmtWhen = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function TeacherFiles({ entry, kind, label, onDownload }) {
  const files = (entry.files || []).filter((f) => f.kind === kind);
  return (
    <div data-testid={`entry-${entry.id}-${kind}`}>
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">{label}</p>
      {files.length === 0 ? (
        <p className="mt-1 text-xs text-slate-400">None</p>
      ) : (
        <div className="mt-1.5 flex flex-wrap gap-2">
          {files.map((file, i) => (
            <button
              key={file.id}
              onClick={() => onDownload(entry, file)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
              data-testid={`classroom-file-${file.id}`}
            >
              <Download size={12} />
              <FileText size={12} />
              {label} {files.length > 1 ? i + 1 : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// "Your homework": own uploads, status, teacher feedback. Starts from the list
// payload and re-reads GET .../homework after any upload/delete/error so the
// lock state always reflects the server (the teacher may mark Done at any time).
function YourHomework({ entry, onStatusChange }) {
  const [state, setState] = useState({
    status: entry.my_homework_status,
    uploads_locked: entry.my_homework_status === 'done',
    max_files: DEFAULT_MAX_FILES,
    files: entry.my_homework_files || [],
    feedback: entry.my_feedback || null,
  });
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await classEntryApi.getMyHomework(entry.id);
      setState(data);
      onStatusChange(entry.id, data.status);
    } catch (err) {
      toast.error(parseApiError(err, 'Could not refresh your homework').message);
    }
  }, [entry.id, onStatusChange]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;
    if (!isPdf(file)) {
      toast.error(NOT_PDF_MESSAGE);
      return;
    }
    const problem = checkFileSize(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setBusy(true);
    try {
      await classEntryApi.uploadMyHomework(entry.id, formData);
      toast.success('Homework uploaded');
    } catch (err) {
      toast.error(parseApiError(err, 'Upload failed').message);
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Delete ${file.original_name}?`)) return;
    setBusy(true);
    try {
      await classEntryApi.deleteMyHomework(entry.id, file.id);
      toast.success('File deleted');
    } catch (err) {
      toast.error(parseApiError(err, 'Failed to delete file').message);
    } finally {
      await refresh();
      setBusy(false);
    }
  };

  const handleDownload = async (file) => {
    try {
      const res = await classEntryApi.downloadMyHomework(entry.id, file.id);
      downloadBlob(res.data, file.original_name);
    } catch (err) {
      toast.error(parseApiError(err, 'Download failed').message);
    }
  };

  const locked = state.uploads_locked;
  const full = state.files.length >= state.max_files;
  const badge = HOMEWORK_BADGE[state.status] || HOMEWORK_BADGE.pending;
  const BadgeIcon = badge.icon;

  return (
    <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40" data-testid={`your-homework-${entry.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-slate-900 dark:text-white">Your homework</p>
        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${badge.color}`}
          data-testid={`homework-status-${entry.id}`}
        >
          <BadgeIcon size={12} /> {badge.label}
        </span>
      </div>

      {state.files.length === 0 ? (
        <p className="mt-2 text-xs text-slate-400">You haven't uploaded anything yet.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {state.files.map((f) => (
            <li
              key={f.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-800"
              data-testid={`my-file-${f.id}`}
            >
              <div className="min-w-0 flex-1 basis-40">
                <p className="break-words text-xs font-medium text-slate-800 dark:text-slate-200">{f.original_name}</p>
                <p className="text-xs text-slate-400">{fmtWhen(f.uploaded_at)}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => handleDownload(f)}
                  aria-label={`Download ${f.original_name}`}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                >
                  <Download size={16} />
                </button>
                {!locked && (
                  <button
                    onClick={() => handleDelete(f)}
                    disabled={busy}
                    aria-label={`Delete ${f.original_name}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-md text-rose-500 hover:bg-rose-50 disabled:opacity-50 dark:hover:bg-rose-900/30"
                    data-testid={`my-file-delete-${f.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {locked ? (
        <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300" data-testid={`locked-note-${entry.id}`}>
          <Lock size={13} className="shrink-0" /> Marked done by your teacher — uploads closed.
        </p>
      ) : (
        <div className="mt-3">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            onChange={handleUpload}
            className="hidden"
            id={`hw-input-${entry.id}`}
            data-testid={`hw-input-${entry.id}`}
          />
          <label
            htmlFor={`hw-input-${entry.id}`}
            className={`inline-flex min-h-[40px] cursor-pointer items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light ${busy || full ? 'pointer-events-none opacity-50' : ''}`}
            data-testid={`hw-upload-btn-${entry.id}`}
          >
            <Upload size={14} /> {busy ? 'Working…' : 'Upload homework'}
          </label>
          <p className="mt-1 text-xs text-slate-400">
            <UploadHint className="" />, up to {state.max_files} files
            {full && <span className="font-medium text-amber-600"> · limit reached, delete one to upload another</span>}
          </p>
        </div>
      )}

      {state.feedback && (
        <div className="mt-3 rounded-md border border-primary/20 bg-primary/5 p-3" data-testid={`feedback-${entry.id}`}>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">Teacher feedback</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800 dark:text-slate-200">{state.feedback.text}</p>
          <p className="mt-1 text-xs text-slate-400">
            {state.feedback.author_name ? `${state.feedback.author_name} · ` : ''}{fmtWhen(state.feedback.feedback_at)}
          </p>
        </div>
      )}
    </div>
  );
}

export default function StudentClassroomPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    classEntryApi.listForStudent()
      .then(({ data }) => setEntries(data))
      .catch((err) => toast.error(parseApiError(err, 'Failed to load classroom entries').message))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const byDate = new Map();
    for (const e of entries) {
      if (!byDate.has(e.date)) byDate.set(e.date, []);
      byDate.get(e.date).push(e);
    }
    return Array.from(byDate.entries()).sort(([a], [b]) => b.localeCompare(a));
  }, [entries]);

  // Deep link from a notification: /student/classroom?entry=<id> scrolls to that
  // card and highlights it. Re-runs when only the query string changes (clicking
  // a notification while already on this page).
  const [searchParams] = useSearchParams();
  const targetId = searchParams.get('entry');
  const [highlightId, setHighlightId] = useState(null);
  useEffect(() => {
    if (!targetId || loading) return undefined;
    if (!entries.some((e) => e.id === targetId)) {
      toast.error('That classroom entry is no longer available.');
      return undefined;
    }
    const el = document.getElementById(`entry-${targetId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setHighlightId(targetId);
    const t = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(t);
  }, [targetId, loading, entries]);

  const handleDownload = useCallback(async (entry, file) => {
    try {
      const res = await classEntryApi.downloadFileAsStudent(entry.id, file.id);
      downloadBlob(res.data, `${entry.title}_${file.kind}${file.id.slice(0, 8)}`);
    } catch (err) {
      toast.error(parseApiError(err, 'Download failed').message);
    }
  }, []);

  // Keep the list payload's status in step with what YourHomework last read.
  const handleStatusChange = useCallback((entryId, status) => {
    setEntries((prev) => prev.map((e) => (e.id === entryId && e.my_homework_status !== status ? { ...e, my_homework_status: status } : e)));
  }, []);

  return (
    <StudentLayout title="Classroom">
      <div className="animate-fade-in space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">Classroom</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Class notes, homework, and your completion status</p>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 rounded-lg bg-slate-100 dark:bg-slate-800" />
            ))}
          </div>
        ) : entries.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-400">No classroom entries yet.</div>
        ) : (
          <div className="space-y-6">
            {grouped.map(([date, items]) => (
              <div key={date}>
                <h3 className="mb-2 text-sm font-semibold text-slate-500 dark:text-slate-400">
                  {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <div className="space-y-3">
                  {items.map((entry) => (
                    <div
                      key={entry.id}
                      id={`entry-${entry.id}`}
                      className={`space-y-3 rounded-lg border bg-white p-4 transition-shadow dark:bg-slate-800 ${
                        highlightId === entry.id
                          ? 'border-primary ring-2 ring-primary/40 dark:border-accent-cool'
                          : 'border-slate-200 dark:border-slate-700'
                      }`}
                      data-testid={`classroom-entry-${entry.id}`}
                      data-highlighted={highlightId === entry.id ? 'true' : 'false'}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                          <span className="inline-flex items-center gap-1.5 font-medium text-slate-900 dark:text-white">
                            <BookOpen size={14} className="text-primary dark:text-accent-cool" />
                            {entry.title}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400">{entry.group_name}</span>
                          <span className="text-slate-500 dark:text-slate-400">by {entry.faculty_name}</span>
                        </div>
                        {entry.notes_message && (
                          <p className="mt-2 break-words text-sm text-slate-600 dark:text-slate-300">{entry.notes_message}</p>
                        )}
                        {entry.faculty_comment && (
                          <p className="mt-1 break-words text-sm italic text-slate-500 dark:text-slate-400">"{entry.faculty_comment}"</p>
                        )}
                      </div>

                      <div className="grid gap-3 sm:grid-cols-2">
                        <TeacherFiles entry={entry} kind="notes" label="Notes" onDownload={handleDownload} />
                        <TeacherFiles entry={entry} kind="homework" label="Homework Sheet" onDownload={handleDownload} />
                      </div>

                      <YourHomework entry={entry} onStatusChange={handleStatusChange} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </StudentLayout>
  );
}
